import React from 'react';
import { Button, MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { ArrowUndo20Regular, Info20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { ContractReview } from '../../../../models/Entities/Contracts/ContractReview';
import { StatusTone } from '../../../../theme/guideTokens';
import { GuideBannerAction } from '../../../../theme/components/GuideButtons';
import { GuideHelpPopover } from '../../../../theme/components/GuideHelpPopover';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { GuideProgress } from '../../../../theme/components/GuideProgress';
import { GuideStepper } from '../../../../theme/components/GuideStepper';
import { formatDateTime, isAutoDecided, isChangeFinding, isReviewActive, WORK_STATES, WorkState, WorkStateCounts, WorkStateFilter } from '../ContractReview.Functions';
import { ReviewStatusPill } from '../ContractReviewPills/ContractReviewPills.Component';

/*
 * Cabecera de la revisión para alguien que no conoce la herramienta: qué pasó (en una frase), en qué paso está
 * (revisó → decidí → aplicá), qué tiene que hacer ahora y el botón para hacerlo. Los números por estado llevan a la lista.
 */

export interface ContractReviewOverviewProps {
  review: ContractReview;
  addendumName?: string;
  counts: WorkStateCounts;
  /** Cambios aceptados con un cambio aplicable que "Aplicar" procesaría. */
  applicableCount: number;
  /** Estado de la lista que se está viendo (marca el número activo). */
  activeState?: WorkStateFilter;
  onGoToState: (state: WorkState) => void;
  /** Acción principal (Aplicar / Crear adenda). Sin `onApply` no se muestra. */
  onApply?: () => void;
  applyLabel: string;
  applyDisabled?: boolean;
  /** Deshacer todo lo aplicado (Contracts 021 §4.2). Sin `onUndo` no se muestra. */
  onUndo?: () => void;
  undoBusy?: boolean;
  onOpenDetails: () => void;
}

const STATE_TONE: Record<WorkState, StatusTone> = { pending: 'warning', accepted: 'success', rejected: 'neutral', applied: 'info' };

export const ContractReviewOverview: React.FC<ContractReviewOverviewProps> = ({
  review,
  addendumName,
  counts,
  applicableCount,
  activeState,
  onGoToState,
  onApply,
  applyLabel,
  applyDisabled,
  onUndo,
  undoBusy,
  onOpenDetails
}) => {
  const { t } = useTranslation();
  const findings = review.findings ?? [];
  const changes = findings.filter(isChangeFinding).length;
  const observations = findings.length - changes;
  const active = isReviewActive(review);
  const failed = review.status === 'Failed';
  const finished = !active && !failed;
  const autoApplied = findings.filter(f => f.applyStatus === 'Applied' && isAutoDecided(f)).length;
  const addendum = addendumName ?? t('contractReview.history.wholeContract');

  const title = failed
    ? t('contractReview.overview.titleFailed')
    : review.status === 'Queued'
      ? t('contractReview.overview.titleQueued')
      : active
        ? t('contractReview.overview.titleRunning')
        : t('contractReview.overview.titleDone');

  // Paso actual: 0 mientras revisa, 1 con algo por decidir, 2 con algo para aplicar, 3 = todo hecho.
  const currentStep = active ? 0 : counts.pending > 0 ? 1 : applicableCount > 0 ? 2 : 3;
  const steps = [
    { key: 'review', label: active ? t('contractReview.overview.step1Running') : t('contractReview.overview.step1') },
    {
      key: 'decide',
      label: t('contractReview.overview.step2'),
      description: counts.pending > 0 ? t('contractReview.overview.step2Detail', { count: counts.pending }) : t('contractReview.overview.step2Done')
    },
    {
      key: 'apply',
      label: t('contractReview.overview.step3'),
      description: applicableCount > 0 ? t('contractReview.overview.step3Detail', { count: applicableCount }) : t('contractReview.overview.step3Done')
    }
  ];

  const nextStep = failed
    ? t('contractReview.overview.nextFailed')
    : counts.pending > 0
      ? t('contractReview.overview.nextDecide', { count: counts.pending })
      : applicableCount > 0
        ? t('contractReview.overview.nextApply', { count: applicableCount, button: applyLabel })
        : findings.length === 0
          ? t('contractReview.overview.nextNothing')
          : t('contractReview.overview.nextDone');

  return (
    <section className="crv-overview" aria-labelledby="review-title" data-testid="review-overview">
      <div className="crv-overview__head">
        <div className="crv-overview__text">
          <div className="crv-overview__title-row">
            <h2 className="crv-overview__title" id="review-title">
              {title}
            </h2>
            <ReviewStatusPill status={review.status} />
          </div>

          {finished && (
            <p className="crv-overview__lead" data-testid="review-lead">
              {findings.length === 0 ? (
                t('contractReview.overview.leadNone', { files: review.fileIds.length, addendum })
              ) : (
                <>
                  {t('contractReview.overview.lead', { files: review.fileIds.length, addendum })} <b>{t('contractReview.overview.leadChanges', { count: changes })}</b>{' '}
                  {t('contractReview.overview.leadAnd')} <b>{t('contractReview.overview.leadObservations', { count: observations })}</b>.
                </>
              )}{' '}
              <GuideHelpPopover title={t('contractReview.overview.helpTitle')} text={t('contractReview.overview.helpText')} className="crv-overview__help" />
            </p>
          )}

          <p className="crv-overview__meta">
            <span>{t('contractReview.overview.requested', { user: review.createdBy, date: formatDateTime(review.created) })}</span>
            {review.appliedAt && (
              <>
                <span className="crv-sep" aria-hidden="true">
                  ·
                </span>
                <span>{t('contractReview.overview.applied', { user: review.appliedBy ?? '—', date: formatDateTime(review.appliedAt) })}</span>
              </>
            )}
          </p>
          {review.instructions && (
            <p className="crv-overview__meta" data-testid="review-instructions">
              <span>{t('contractReview.overview.instructions')}:</span> <q>{review.instructions}</q>
            </p>
          )}
        </div>

        <div className="crv-overview__actions">
          <Button appearance="subtle" icon={<Info20Regular />} onClick={onOpenDetails} data-testid="review-details-open">
            {t('contractReview.overview.details')}
          </Button>
          {onUndo && (
            <Button appearance="secondary" icon={<ArrowUndo20Regular />} disabled={undoBusy} onClick={onUndo} data-testid="review-undo">
              {t('contractReview.overview.undoAll')}
            </Button>
          )}
          {onApply && (
            <Button appearance="primary" size="large" disabled={applyDisabled} onClick={onApply} data-testid="review-apply">
              {applyLabel}
            </Button>
          )}
        </div>
      </div>

      {active && (
        <GuideProgress
          value={review.status === 'Queued' ? undefined : review.progressPercent}
          label={review.stage ? t(`contractReview.stage.${review.stage}`, review.stage) : t('contractReview.status.Queued')}
          detail={t('contractReview.page.running', { stage: review.stage ? t(`contractReview.stage.${review.stage}`, review.stage) : '—', value: review.progressPercent })}
          ariaLabel={t('contractReview.history.progress', { value: review.progressPercent })}
          data-testid="review-progress"
        />
      )}

      {!failed && (
        <GuideStepper
          steps={steps}
          current={currentStep}
          ariaLabel={t('contractReview.overview.stepsLabel')}
          doneLabel={t('contractReview.overview.stepDone')}
          className="crv-overview__steps"
          data-testid="review-steps"
        />
      )}

      {finished && findings.length > 0 && (
        <div className="crv-counts" role="group" aria-label={t('contractReview.overview.summaryLabel')} data-testid="review-summary">
          {WORK_STATES.map(state => {
            const pressed = activeState === state;
            return (
              <button
                key={state}
                type="button"
                className="crv-count"
                data-tone={counts[state] > 0 ? STATE_TONE[state] : 'neutral'}
                aria-pressed={pressed}
                onClick={() => onGoToState(state)}
                data-testid={`review-count-${state}`}
              >
                <span className="crv-count__value">{counts[state]}</span>
                <span className="crv-count__label">{t(`contractReview.work.state.${state}`, { count: counts[state] }).replace(/\s*\(\d+\)\s*$/, '')}</span>
              </button>
            );
          })}
        </div>
      )}

      {!active && (
        <p className="crv-next" data-testid="review-next-step">
          <b>{t('contractReview.overview.nextLabel')}:</b> {nextStep}
        </p>
      )}

      {autoApplied > 0 && (
        <GuideMessageBar intent="info" layout="multiline" data-testid="review-auto-applied">
          <MessageBarBody>
            <MessageBarTitle>{t('contractReview.autoApply.bannerTitle', { count: autoApplied })}</MessageBarTitle>
            {t('contractReview.autoApply.bannerText')}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => onGoToState('applied')}>{t('contractReview.overview.autoAppliedAction')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {review.status === 'Reverted' && (
        <GuideMessageBar intent="info" layout="multiline" data-testid="review-reverted">
          <MessageBarBody>{t('contractReview.undo.revertedBanner')}</MessageBarBody>
        </GuideMessageBar>
      )}
    </section>
  );
};

export default ContractReviewOverview;
